'use client';

import { useEffect, useState } from 'react';

/**
 * Hold-still capture gate.
 *
 * Auto-capture used to fire a few hundred ms after the first 'ready' frame,
 * so a face that only briefly met the thresholds could be captured while it
 * was barely settled in the guide. This gate requires `ready` to stay
 * continuously true while the overlay marker flashes
 * HOLD_STILL_FLASH_COUNT times (one HOLD_STILL_FLASH_INTERVAL_MS apart) —
 * the flashes double as the user's "keep holding" countdown. Any drop in
 * `ready` (face lost, quality dip, capture paused) resets the sequence.
 *
 * Sessions that run a liveness/challenge flow should pass `enabled: false`:
 * the gate becomes a pass-through so the flashing countdown never competes
 * with challenge prompts for the user's attention.
 */

/** Number of marker flashes in the hold-still countdown. */
export const HOLD_STILL_FLASH_COUNT = 3;
/** Spacing between flashes (ms). */
export const HOLD_STILL_FLASH_INTERVAL_MS = 500;

export interface HoldStillGate {
  /**
   * `ready` has been held through the full flash sequence — auto-capture may
   * fire. When the gate is disabled this is a pass-through of `ready`.
   */
  armed: boolean;
  /**
   * Pulse counter — 0 while idle, incremented once per flash while holding.
   * Feed to FaceMeshOverlay's `flashPulse` prop to render the countdown.
   */
  flashPulse: number;
  /** Ready and mid-countdown but not yet armed — drive "Hold still" copy. */
  holding: boolean;
}

interface HoldProgress {
  /** Whether the countdown sequence is currently live. */
  active: boolean;
  /** Pulses fired so far this sequence. */
  pulse: number;
  /** Full sequence completed — `ready` held for the whole countdown. */
  held: boolean;
}

export function useHoldStillGate(ready: boolean, enabled = true): HoldStillGate {
  const gating = enabled && ready;
  const [hold, setHold] = useState<HoldProgress>({ active: false, pulse: 0, held: false });

  // Render-time adjustment (the sanctioned prev-state pattern): the
  // countdown resets the moment the hold is lost or the gate is disabled —
  // before any consumer can read a stale armed value.
  if (hold.active !== gating) {
    setHold({ active: gating, pulse: 0, held: false });
  }

  useEffect(() => {
    if (!hold.active) return;

    const timers: number[] = [];
    // First flash on the next tick — instant "hold it right there" feedback.
    timers.push(window.setTimeout(() => {
      setHold((h) => (h.active ? { ...h, pulse: 1 } : h));
    }, 0));
    for (let pulse = 2; pulse <= HOLD_STILL_FLASH_COUNT; pulse += 1) {
      timers.push(window.setTimeout(() => {
        setHold((h) => (h.active ? { ...h, pulse } : h));
      }, (pulse - 1) * HOLD_STILL_FLASH_INTERVAL_MS));
    }
    // Arm one interval after the final flash so it is actually seen —
    // ~1.5s of sustained readiness in total.
    timers.push(window.setTimeout(() => {
      setHold((h) => (h.active ? { ...h, held: true } : h));
    }, HOLD_STILL_FLASH_COUNT * HOLD_STILL_FLASH_INTERVAL_MS));

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [hold.active]);

  return {
    armed: enabled ? gating && hold.held : ready,
    flashPulse: enabled && hold.active ? hold.pulse : 0,
    holding: gating && !hold.held,
  };
}
